import React from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * RiskIndicator
 *
 * Reusable card component for summarising chokepoint or country risk.
 * Designed to be fed by backend scores or page-level assumptions.
 *
 * Props:
 * - level: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL"
 * - score: optional numeric score (0–100) for additional context
 * - lastUpdated: ISO string or Date for the timestamp
 * - headline: short 1–2 line narrative of current risk state
 * - drivers: array of bullet-point drivers (strings)
 * - timeHorizon: optional label such as "Next 30 days"
 */
const RiskIndicator = ({
  level = 'MEDIUM',
  score,
  lastUpdated,
  headline,
  drivers = [],
  timeHorizon = 'Next 30 days',
}) => {
  const normalizedLevel = String(level || '').toUpperCase();

  const levelClasses = (() => {
    switch (normalizedLevel) {
      case 'LOW':
        return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
      case 'MEDIUM':
        return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
      case 'HIGH':
        return 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
      case 'CRITICAL':
        return 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-slate-100 text-slate-700 dark:bg-surface-container-high dark:text-on-surface-variant';
    }
  })();

  const formattedTimestamp =
    lastUpdated instanceof Date
      ? lastUpdated.toLocaleString()
      : lastUpdated
      ? new Date(lastUpdated).toLocaleString()
      : null;

  return (
    <aside className="flex h-full flex-col rounded-stitch-xl border border-slate-200/80 bg-white p-6 shadow-sm dark:border-outline-variant/15 dark:bg-surface-container">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-slate-800 dark:text-on-surface">Live Risk Indicator</h3>
          <p className="text-xs text-slate-500 dark:text-on-surface-variant">
            {timeHorizon ? `Assessment horizon: ${timeHorizon}` : 'Composite risk assessment'}
          </p>
        </div>
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${levelClasses}`}
        >
          <AlertTriangle className="w-3 h-3 mr-1" />
          {normalizedLevel || 'RISK'}
        </span>
      </div>

      <div className="mb-3">
        {typeof score === 'number' && !Number.isNaN(score) && (
          <p className="font-headline text-3xl font-bold text-slate-900 dark:text-on-surface">
            {score.toFixed(1)}{' '}
            <span className="text-base font-normal text-slate-500 dark:text-outline">/ 100</span>
          </p>
        )}
        {formattedTimestamp && (
          <p className="mt-1 text-xs text-slate-600 dark:text-on-surface-variant">
            Last updated: {formattedTimestamp}
          </p>
        )}
      </div>

      {headline && (
        <p className="mb-3 text-sm text-slate-800 dark:text-on-surface">
          {headline}
        </p>
      )}

      {drivers && drivers.length > 0 && (
        <ul className="mb-3 list-inside list-disc space-y-1 text-xs text-slate-700 dark:text-on-surface-variant">
          {drivers.map((driver, idx) => (
            <li key={idx}>{driver}</li>
          ))}
        </ul>
      )}

      <p className="mt-auto border-t border-dashed border-slate-200 pt-2 text-[0.65rem] text-slate-500 dark:border-outline-variant/20 dark:text-outline">
        This indicator blends qualitative and quantitative inputs. It is illustrative and should be
        combined with your own risk frameworks.
      </p>
    </aside>
  );
};

export default RiskIndicator;

